<template>
  <transition name="credits-recharge-fade">
    <div
      v-if="visible"
      class="credits-recharge-root fixed inset-0 z-[10055] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="积分充值"
    >
      <div class="absolute inset-0 bg-black/75 backdrop-blur-md" @click="onClose"></div>
      <div
        class="credits-recharge-panel relative z-10 flex max-h-[92vh] w-full max-w-[720px] flex-col overflow-hidden rounded-[20px] border border-white/[0.06] bg-[#0a0a0c] text-white shadow-2xl"
        @click.stop
      >
        <div class="flex shrink-0 items-center justify-between px-6 pb-2 pt-5">
          <h2 class="text-[18px] font-bold tracking-tight text-white">积分充值</h2>
          <button
            type="button"
            class="flex h-8 w-8 items-center justify-center rounded-full bg-white/[0.06] text-white/50 transition hover:bg-white/10 hover:text-white"
            aria-label="关闭"
            @click="onClose"
          >
            <i class="fas fa-times text-[13px]"></i>
          </button>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto px-6 pb-4 [-ms-overflow-style:none] [scrollbar-width:thin]">
          <!-- 用户信息条（布局不变） -->
          <div
            class="crm-user-bar mb-5 flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-white/[0.06] bg-white/[0.03] px-4 py-3"
          >
            <div class="flex min-w-0 items-center gap-3">
              <div
                class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-[#161618]"
              >
                <img v-if="avatar" :src="avatar" alt="" class="h-full w-full object-cover" />
                <i v-else class="fas fa-user text-sm text-white/50"></i>
              </div>
              <div class="min-w-0">
                <div class="truncate text-[14px] font-medium text-white/95">{{ userName }}</div>
                <div class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-white/40">
                  <span class="shrink-0">目前是</span>
                  <span
                    class="crm-vip-badge inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-none"
                    :class="vipnameBadgeClass"
                  >
                    {{ vipname || 'FREE' }}
                  </span>
                  <span v-if="vipEndAt" class="text-white/35">
                    有效期至 {{ formatVipEndAtDisplay(vipEndAt) }}
                  </span>
                  <button
                    v-if="!isMember"
                    type="button"
                    class="text-sky-300/90 transition hover:text-sky-200"
                    @click="$emit('upgrade-member')"
                  >
                    升级会员 &gt;
                  </button>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-1.5 text-[13px]">
              <span class="text-white/45">积分</span>
              <span class="crm-coin-icon inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center">
                <i class="fas fa-coins text-[11px] text-amber-300"></i>
              </span>
              <span class="font-semibold tabular-nums text-white/90">{{ formatPoints(balance) }}</span>
            </div>
          </div>

          <!-- 档位网格（布局不变：2/4 列） -->
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div
              v-for="pack in packs"
              :key="pack.id"
              class="crm-pack-wrap"
              :class="[
                pack.featured ? 'sm:col-span-2' : '',
                selectedId === pack.id ? 'crm-pack-wrap--selected' : ''
              ]"
            >
              <button
                type="button"
                class="crm-pack-card relative flex h-full w-full flex-col rounded-[14px] border border-transparent px-3 py-4 text-left transition"
                :class="pack.featured ? 'crm-pack-card--featured' : 'crm-pack-card--standard'"
                @click="selectPack(pack)"
              >
                <span
                  v-if="pack.memberBonus"
                  class="absolute right-2 top-2 z-10 rounded-md bg-gradient-to-r from-amber-400/90 to-amber-500/90 px-1.5 py-0.5 text-[10px] font-bold text-[#1a1208]"
                >
                  {{ formatBonusLabel(pack.memberBonus) }}
                </span>

                <div class="flex flex-1 flex-col justify-between">
                  <div class="pt-1">
                    <div class="flex items-baseline gap-1">
                      <span class="text-[26px] font-bold leading-none tabular-nums tracking-tight text-white">
                        <template v-if="pack.pointsOld">
                          <span
                            class="mr-1 text-[14px] font-medium text-white/30 line-through decoration-white/20"
                            >{{ formatPoints(pack.pointsOld) }}</span
                          >
                        </template>
                        {{ formatPoints(pack.points) }}
                      </span>
                      <span class="crm-coin-icon mb-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center">
                        <i class="fas fa-coins text-[10px] text-amber-300/95"></i>
                      </span>
                    </div>
                    <div class="mt-1 text-[12px] text-white/40">积分</div>
                  </div>
                  <div class="mt-3 flex justify-end">
                    <span class="text-[15px] font-semibold tabular-nums text-white/90">¥{{ pack.price }}</span>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <!-- 温馨提示 -->
          <div class="mt-5 space-y-1.5 text-[11px] leading-relaxed text-white/32">
            <p>
              <span class="text-white/45">温馨提示：</span>
              只有成为会员，才能享受视频加速、去水印等权益；充值积分永久有效（赠送积分 31 天内有效）。
            </p>
            <p>
              如遇充值问题，请联系
              <a class="text-white/50 hover:text-sky-300/90" href="mailto:pluskeji@126.com">pluskeji@126.com</a>
            </p>
            <!-- <p>
              <a
                class="text-white/50 hover:text-sky-300/90"
                href="https://www.plus.ai"
                target="_blank"
                rel="noopener noreferrer"
                >积分规则</a
              >
            </p> -->
          </div>
        </div>

        <div class="shrink-0 border-t border-white/[0.05] px-6 py-4">
          <p class="mb-3 text-center text-[11px] text-white/35 sm:text-left">
            *充值积分 2 年内有效，支付后不退不换
          </p>
          <div class="flex justify-center sm:justify-end">
            <button
              type="button"
              class="crm-pay-btn inline-flex min-w-[200px] items-center justify-center gap-2 rounded-full px-8 py-3 text-[15px] font-bold text-[#0c0c0e] transition active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-45"
              :disabled="!selectedId || submitting"
              @click="onPurchase"
            >
              <span class="crm-pay-play" aria-hidden="true">
                <i class="fas fa-play text-[9px] translate-x-[0.5px]"></i>
              </span>
              {{ submitting ? '提交中…' : '去支付' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { formatVipEndAt, getVipnameBadgeClass } from '../utils/creditsRechargeApi'

export default {
  name: 'CreditsRechargeModal',
  props: {
    visible: { type: Boolean, default: false },
    userName: { type: String, default: '用户' },
    avatar: { type: String, default: '' },
    vipname: { type: String, default: 'FREE' },
    vipEndAt: { type: String, default: '' },
    levelLabel: { type: String, default: 'FREE 工作室' },
    isMember: { type: Boolean, default: false },
    balance: { type: Number, default: 0 },
    packs: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    submitting: { type: Boolean, default: false },
    selectedId: { type: String, default: '' }
  },
  computed: {
    vipnameBadgeClass() {
      return getVipnameBadgeClass(this.vipname || this.levelLabel, this.isMember)
    }
  },
  watch: {
    visible(v) {
      if (v && typeof document !== 'undefined') document.body.style.overflow = 'hidden'
      else if (typeof document !== 'undefined') document.body.style.overflow = ''
    }
  },
  beforeDestroy() {
    if (typeof document !== 'undefined') document.body.style.overflow = ''
  },
  methods: {
    formatVipEndAtDisplay(s) {
      return formatVipEndAt(s)
    },
    formatPoints(n) {
      const v = Number(n)
      if (!Number.isFinite(v)) return '0'
      return v.toLocaleString('en-US')
    },
    formatBonusLabel(bonus) {
      const s = String(bonus || '').trim()
      if (!s) return ''
      if (/^多\s*/.test(s) || /%/.test(s)) return s
      return `多 ${s}`
    },
    selectPack(pack) {
      this.$emit('select', pack)
    },
    onClose() {
      this.$emit('close')
    },
    onPurchase() {
      if (!this.selectedId || this.submitting) return
      this.$emit('purchase')
    }
  }
}
</script>

<style scoped>
.credits-recharge-fade-enter-active,
.credits-recharge-fade-leave-active {
  transition: opacity 0.22s ease;
}
.credits-recharge-fade-enter,
.credits-recharge-fade-leave-to {
  opacity: 0;
}
.credits-recharge-panel {
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
}

.crm-coin-icon {
  filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.35));
}

.crm-vip-badge--free {
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.crm-vip-badge--standard {
  color: #6ee7b7;
  background: rgba(16, 185, 129, 0.14);
  border: 1px solid rgba(52, 211, 153, 0.28);
  box-shadow: 0 0 12px rgba(16, 185, 129, 0.12);
}
.crm-vip-badge--pro {
  color: #fdba74;
  background: rgba(251, 146, 60, 0.14);
  border: 1px solid rgba(251, 146, 60, 0.3);
  box-shadow: 0 0 12px rgba(251, 146, 60, 0.12);
}
.crm-vip-badge--flagship {
  color: #fda4af;
  background: rgba(244, 63, 94, 0.14);
  border: 1px solid rgba(248, 113, 113, 0.32);
  box-shadow: 0 0 12px rgba(244, 63, 94, 0.14);
}
.crm-vip-badge--member {
  color: #7dd3fc;
  background: rgba(56, 189, 248, 0.12);
  border: 1px solid rgba(125, 211, 252, 0.28);
}

/* 档位外框：图2 选中渐变描边 */
.crm-pack-wrap {
  border-radius: 15px;
  padding: 1px;
  background: transparent;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}
.crm-pack-wrap--selected {
  background: linear-gradient(135deg, #22d3ee 0%, #fb923c 48%, #f472b6 100%);
  box-shadow:
    0 0 20px rgba(34, 211, 238, 0.2),
    0 0 28px rgba(251, 146, 60, 0.12);
}
.crm-pack-wrap:not(.crm-pack-wrap--selected):hover {
  background: rgba(255, 255, 255, 0.08);
}

.crm-pack-card {
  min-height: 112px;
  background: linear-gradient(160deg, rgba(28, 28, 32, 0.95) 0%, rgba(14, 14, 16, 0.98) 100%);
}
.crm-pack-card--standard::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255, 255, 255, 0.04), transparent 70%);
  pointer-events: none;
}
.crm-pack-card--featured {
  background: linear-gradient(155deg, rgba(32, 28, 38, 0.98) 0%, rgba(12, 12, 14, 0.99) 100%);
}
.crm-pack-card--featured::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: radial-gradient(circle at 30% 20%, rgba(96, 165, 250, 0.12), transparent 45%),
    radial-gradient(circle at 70% 80%, rgba(251, 191, 36, 0.1), transparent 50%);
  pointer-events: none;
}

/* 去支付：图2 浅色渐变按钮 */
.crm-pay-btn {
  background: linear-gradient(90deg, #a5f3fc 0%, #fef3c7 42%, #fde68a 100%);
  box-shadow:
    0 4px 24px rgba(165, 243, 252, 0.25),
    0 2px 8px rgba(0, 0, 0, 0.35);
}
.crm-pay-btn:not(:disabled):hover {
  filter: brightness(1.05);
  box-shadow:
    0 6px 28px rgba(165, 243, 252, 0.35),
    0 2px 10px rgba(0, 0, 0, 0.4);
}
.crm-pay-play {
  display: inline-flex;
  height: 22px;
  width: 22px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: linear-gradient(135deg, #22d3ee, #fb923c);
  color: #0c0c0e;
}
</style>
